import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { adminAuthCheck, adminNumbeoGet, adminNumbeoUpdate } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import DataErrorNotice from "../components/DataErrorNotice";
import { toast } from "sonner";
import { Lock, AlertCircle, ExternalLink, Save, RotateCcw, ChevronDown, ChevronRight, Loader2, BadgeCheck } from "lucide-react";
import { NUMBEO_CATEGORY_META } from "../components/NumbeoSections";

const STORAGE_KEY = "hejpoland_admin_secret";
const AXES = ["min", "avg", "max"];

// Permissive regex for in-progress typing: empty, optional sign, digits, single decimal sep.
// Accepts intermediate states like "14", "14.", "14.3", "14,3", "-1.5", ".5", "".
const NUMERIC_RE = /^-?\d*[.,]?\d*$/;
const isAcceptableInput = (s) => s === "" || NUMERIC_RE.test(s);

/** Display a number (or null) as a string for editing — no formatting tricks, just toString. */
function numberToString(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

/**
 * Convert a typed string to a finite number or null.
 * Returns { ok: true, value: number|null } on success, { ok: false } when the
 * string can't be turned into a finite number (e.g. "14." or "abc").
 */
function stringToNumber(s) {
  const trimmed = String(s ?? "").trim().replace(",", ".");
  if (trimmed === "") return { ok: true, value: null };
  // Reject obviously incomplete pieces: lone dot, lone sign, sign + dot.
  if (trimmed === "." || trimmed === "-" || trimmed === "-.") return { ok: false };
  // Reject trailing dot ("14.") — incomplete decimal.
  if (trimmed.endsWith(".")) return { ok: false };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { ok: false };
  return { ok: true, value: n };
}

/** Comparison used for the "dirty" indicator: numeric when possible, falls back to raw string. */
function stringsDifferAsValues(a, b) {
  const ra = stringToNumber(a);
  const rb = stringToNumber(b);
  if (ra.ok && rb.ok) return ra.value !== rb.value;
  // If either side can't parse, treat as dirty so the user sees feedback while typing.
  return String(a ?? "") !== String(b ?? "");
}

/** Convert an indicators tree (with numeric min/avg/max) into a string-backed editable draft. */
function indicatorsToDraft(indicators) {
  const out = {};
  for (const [cat, items] of Object.entries(indicators || {})) {
    out[cat] = (items || []).map((it) => ({
      ...it,
      min: numberToString(it.min),
      avg: numberToString(it.avg),
      max: numberToString(it.max),
    }));
  }
  return out;
}

/** Convert the string-backed draft back to a numeric tree for the API. Aggregates parse errors. */
function draftToPayload(draftInd) {
  const out = {};
  const errors = []; // cell ids that failed to parse
  for (const [cat, items] of Object.entries(draftInd)) {
    out[cat] = (items || []).map((it) => {
      const cells = {};
      for (const axis of AXES) {
        const r = stringToNumber(it[axis]);
        if (!r.ok) errors.push(`${cat}.${it.key}.${axis}`);
        cells[axis] = r.ok ? r.value : null;
      }
      return {
        key: it.key,
        label_fr: it.label_fr,
        label_en: it.label_en,
        unit: it.unit,
        ...cells,
      };
    });
  }
  return { indicators: out, errors };
}

export default function AdminNumbeoEdit() {
  const { t, lp } = useT();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [secret, setSecret] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [authError, setAuthError] = useState("");

  const [initialData, setInitialData] = useState(null);  // server snapshot
  const [draftMeta, setDraftMeta] = useState({ contributors_12m: "", entries_12m: "", numbeo_last_update: "" });
  const [draftInd, setDraftInd] = useState({});         // string-backed
  const [expanded, setExpanded] = useState(() => Object.fromEntries(NUMBEO_CATEGORY_META.map((c) => [c.key, true])));
  const [loadErr, setLoadErr] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!secret) return;
    adminAuthCheck(secret).then(() => setAuthed(true)).catch(() => {
      sessionStorage.removeItem(STORAGE_KEY); setSecret("");
    });
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    if (!authed) return;
    setLoadErr(false);
    try {
      const data = await adminNumbeoGet(slug, secret);
      setInitialData(data);
      setDraftMeta({
        contributors_12m: numberToString(data.numbeo.meta?.contributors_12m),
        entries_12m:      numberToString(data.numbeo.meta?.entries_12m),
        numbeo_last_update: data.numbeo.meta?.numbeo_last_update ?? "",
      });
      setDraftInd(indicatorsToDraft(data.numbeo.indicators));
    } catch (e) {
      console.error("[AdminNumbeoEdit] load failed:", e?.message);
      setLoadErr(true);
    }
  }, [authed, secret, slug]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault(); setAuthError("");
    try {
      await adminAuthCheck(pwInput);
      sessionStorage.setItem(STORAGE_KEY, pwInput);
      setSecret(pwInput); setAuthed(true); setPwInput("");
    } catch { setAuthError(t("adminSources.invalidSecret")); }
  };

  // ------------ Field updaters ------------
  // Indicator cells store strings. We *only* accept the keystroke if the resulting
  // string still looks like a partial number — otherwise the keystroke is ignored.
  const updateIndCell = (catKey, indKey, axis, raw) => {
    if (!isAcceptableInput(raw)) return;
    setDraftInd((prev) => ({
      ...prev,
      [catKey]: (prev[catKey] || []).map((it) => (it.key === indKey ? { ...it, [axis]: raw } : it)),
    }));
  };

  // Meta numeric fields (integers) use the same string-backed strategy.
  const updateMetaNumber = (field, raw) => {
    if (!isAcceptableInput(raw)) return;
    setDraftMeta((m) => ({ ...m, [field]: raw }));
  };
  const updateMetaDate = (raw) => {
    setDraftMeta((m) => ({ ...m, numbeo_last_update: raw || "" }));
  };

  // ------------ Diff vs initial ------------
  const { modifiedCount, modifiedCells, invalidCells } = useMemo(() => {
    if (!initialData) return { modifiedCount: 0, modifiedCells: new Set(), invalidCells: new Set() };
    const dirty = new Set();
    const invalid = new Set();
    let count = 0;
    const oldInd = initialData.numbeo.indicators || {};
    for (const cat of Object.keys(draftInd)) {
      const oldItems = Object.fromEntries((oldInd[cat] || []).map((i) => [i.key, i]));
      for (const it of draftInd[cat] || []) {
        for (const axis of AXES) {
          const newRaw = it[axis];
          const oldRawAsStr = numberToString(oldItems[it.key]?.[axis] ?? null);
          if (stringsDifferAsValues(newRaw, oldRawAsStr)) {
            dirty.add(`${cat}.${it.key}.${axis}`);
            count++;
          }
          // A non-empty unparseable string is invalid (e.g. "14.")
          if (newRaw !== "" && !stringToNumber(newRaw).ok) {
            invalid.add(`${cat}.${it.key}.${axis}`);
          }
        }
      }
    }
    const oldMeta = initialData.numbeo.meta || {};
    if (stringsDifferAsValues(draftMeta.contributors_12m, numberToString(oldMeta.contributors_12m ?? null))) {
      count++; dirty.add("meta.contributors_12m");
    }
    if (stringsDifferAsValues(draftMeta.entries_12m, numberToString(oldMeta.entries_12m ?? null))) {
      count++; dirty.add("meta.entries_12m");
    }
    if ((draftMeta.numbeo_last_update || "") !== (oldMeta.numbeo_last_update || "")) {
      count++; dirty.add("meta.numbeo_last_update");
    }
    // Meta numeric invalidity
    if (draftMeta.contributors_12m !== "" && !stringToNumber(draftMeta.contributors_12m).ok) invalid.add("meta.contributors_12m");
    if (draftMeta.entries_12m       !== "" && !stringToNumber(draftMeta.entries_12m).ok)       invalid.add("meta.entries_12m");
    return { modifiedCount: count, modifiedCells: dirty, invalidCells: invalid };
  }, [draftInd, draftMeta, initialData]);

  const reset = () => {
    if (!initialData) return;
    setDraftMeta({
      contributors_12m: numberToString(initialData.numbeo.meta?.contributors_12m),
      entries_12m:      numberToString(initialData.numbeo.meta?.entries_12m),
      numbeo_last_update: initialData.numbeo.meta?.numbeo_last_update ?? "",
    });
    setDraftInd(indicatorsToDraft(initialData.numbeo.indicators));
  };

  const save = async () => {
    if (invalidCells.size > 0) {
      toast.error(`${t("adminNumbeo.saveError")} — ${invalidCells.size} valeur(s) invalide(s)`);
      return;
    }
    setSaving(true);
    try {
      const { indicators, errors } = draftToPayload(draftInd);
      if (errors.length > 0) {
        toast.error(`${t("adminNumbeo.saveError")} — ${errors.length} valeur(s) invalide(s)`);
        setSaving(false);
        return;
      }
      const parseMeta = (raw) => {
        const r = stringToNumber(raw);
        return r.ok ? r.value : null;
      };
      const payload = {
        numbeo_url: initialData.numbeo.url,
        meta: {
          contributors_12m: parseMeta(draftMeta.contributors_12m),
          entries_12m: parseMeta(draftMeta.entries_12m),
          numbeo_last_update: draftMeta.numbeo_last_update || null,
        },
        indicators,
      };
      const resp = await adminNumbeoUpdate(slug, payload, secret);
      toast.success(`${t("adminNumbeo.savedToast")} (${resp.changes_count})`);
      setInitialData({ ...initialData, numbeo: resp.numbeo });
      setDraftMeta({
        contributors_12m: numberToString(resp.numbeo.meta?.contributors_12m),
        entries_12m:      numberToString(resp.numbeo.meta?.entries_12m),
        numbeo_last_update: resp.numbeo.meta?.numbeo_last_update ?? "",
      });
      setDraftInd(indicatorsToDraft(resp.numbeo.indicators));
    } catch (e) {
      console.error("[AdminNumbeoEdit] save failed:", e?.message, e?.response?.data);
      toast.error(e.response?.data?.detail || t("adminNumbeo.saveError"));
    } finally { setSaving(false); }
  };

  // ------------ Render ------------
  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24" data-testid="admin-numbeo-edit-login">
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="w-12 h-12 rounded-2xl bg-[#0056D2]/10 flex items-center justify-center mb-5"><Lock className="w-5 h-5 text-[#0056D2]" /></div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">{t("adminNumbeo.editTitle")}</h1>
          <p className="text-sm text-slate-600 mt-2 mb-6">{t("adminSources.needSecret")}.</p>
          <form onSubmit={login} className="space-y-3">
            <input type="password" autoFocus value={pwInput} onChange={(e) => setPwInput(e.target.value)} placeholder={t("adminSources.promptSecret")} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent outline-none" data-testid="admin-numbeo-edit-secret-input" />
            {authError && <div className="flex items-center gap-2 text-sm text-[#DC143C]"><AlertCircle className="w-4 h-4" />{authError}</div>}
            <button type="submit" className="w-full bg-[#0056D2] hover:bg-[#0047AD] text-white font-medium py-2.5 rounded-full transition" data-testid="admin-numbeo-edit-login-submit">Se connecter</button>
          </form>
        </div>
      </div>
    );
  }

  if (loadErr) return <div className="max-w-3xl mx-auto px-4 py-16"><DataErrorNotice onRetry={load} /></div>;
  if (!initialData) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">{t("city.loading")}</div>;

  const city = initialData;
  const numbeoUrl = city.numbeo.url;
  const saveDisabled = saving || modifiedCount === 0 || invalidCells.size > 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 md:py-14" data-testid="admin-numbeo-edit-page">
      {/* Header / breadcrumb */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to={lp("/admin/numbeo")} className="text-sm text-slate-500 hover:text-[#0056D2] inline-flex items-center gap-1" data-testid="back-to-list">
            {t("adminNumbeo.backToList")}
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-2">
            {t("adminNumbeo.editTitle")} — {city.name_fr}
          </h1>
          <p className="text-xs text-slate-400 mt-1">{city.slug} · {city.name_pl}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {numbeoUrl && (
            <a href={numbeoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium hover:bg-amber-100 transition" data-testid="open-numbeo-source">
              <ExternalLink className="w-3.5 h-3.5" /> {t("adminNumbeo.openNumbeo")}
            </a>
          )}
          <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
            <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
            {t("adminNumbeo.completenessLabel")} : <span className="font-medium text-slate-700">{city.numbeo.completeness?.percentage ?? "—"}%</span>
            {city.numbeo.history?.length > 0 && (
              <span className="ml-3 text-slate-400">· {t("adminNumbeo.historyLabel")} : {city.numbeo.history.length}/12</span>
            )}
          </p>
        </div>
      </div>

      {/* Meta block */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-8" data-testid="meta-block">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-4">{t("adminNumbeo.metaTitle")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* contributors_12m + entries_12m use text inputs with decimal inputMode */}
          {[
            { f: "contributors_12m", label: t("adminNumbeo.metaContributors") },
            { f: "entries_12m",      label: t("adminNumbeo.metaEntries") },
          ].map(({ f, label }) => {
            const isDirty = modifiedCells.has(`meta.${f}`);
            const isInvalid = invalidCells.has(`meta.${f}`);
            return (
              <div key={f}>
                <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">{label}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={draftMeta[f]}
                  onChange={(e) => updateMetaNumber(f, e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#0056D2] ${
                    isInvalid ? "bg-red-50 border-red-300" : isDirty ? "bg-yellow-50 border-yellow-300" : "bg-white border-slate-200"
                  }`}
                  data-testid={`meta-${f}`}
                />
              </div>
            );
          })}
          <div>
            <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">{t("adminNumbeo.metaLastUpdate")}</label>
            <input
              type="date"
              value={draftMeta.numbeo_last_update}
              onChange={(e) => updateMetaDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#0056D2] ${
                modifiedCells.has("meta.numbeo_last_update") ? "bg-yellow-50 border-yellow-300" : "bg-white border-slate-200"
              }`}
              data-testid="meta-numbeo_last_update"
            />
          </div>
        </div>
      </section>

      {/* Indicator categories */}
      <div className="space-y-4 mb-8">
        {NUMBEO_CATEGORY_META.map(({ key, emoji, labelKey }) => {
          const items = draftInd[key] || [];
          if (items.length === 0) return null;
          const isOpen = expanded[key];
          const catModifiedCount = [...modifiedCells].filter((c) => c.startsWith(`${key}.`)).length;
          return (
            <section key={key} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`edit-category-${key}`}>
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50"
                data-testid={`toggle-${key}`}
              >
                <span className="flex items-center gap-2 font-display text-base font-semibold text-slate-900">
                  <span className="text-xl" aria-hidden="true">{emoji}</span>
                  {t(labelKey)}
                  <span className="text-xs text-slate-400 font-normal ml-1">({items.length})</span>
                  {catModifiedCount > 0 && (
                    <span className="ml-2 text-[10px] bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-full px-2 py-0.5 font-medium">{catModifiedCount} modif.</span>
                  )}
                </span>
                {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
              {isOpen && (
                <div className="overflow-x-auto border-t border-slate-100">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Libellé</th>
                        {AXES.map((a) => (
                          <th key={a} className="px-4 py-2 text-right font-medium uppercase tracking-wide text-[10px]">
                            {t(`adminNumbeo.indicator${a[0].toUpperCase()}${a.slice(1)}`)}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-center font-medium text-[10px]">{items[0]?.unit ? `Unité (${items[0].unit})` : "Unité"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => {
                        // Coherence warning uses parsed numeric values only when all three are valid.
                        const parsed = AXES.map((a) => stringToNumber(it[a]));
                        const allOk = parsed.every((p) => p.ok);
                        const allNotNull = parsed.every((p) => p.value !== null);
                        const incoherent = allOk && allNotNull && !(parsed[0].value <= parsed[1].value && parsed[1].value <= parsed[2].value);
                        return (
                          <tr key={it.key} className="border-t border-slate-100">
                            <td className="px-4 py-2 text-slate-700">
                              <span>{it.label_fr}</span>
                              {incoherent && (
                                <span className="ml-2 text-[10px] text-amber-700 inline-flex items-center gap-1" title={t("adminNumbeo.warnMinAvgMax")}>
                                  <AlertCircle className="w-3 h-3" /> {t("adminNumbeo.warnMinAvgMax")}
                                </span>
                              )}
                            </td>
                            {AXES.map((axis) => {
                              const cellKey = `${key}.${it.key}.${axis}`;
                              const isDirty = modifiedCells.has(cellKey);
                              const isInvalid = invalidCells.has(cellKey);
                              return (
                                <td key={axis} className="px-2 py-1.5 text-right">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={it[axis]}
                                    onChange={(e) => updateIndCell(key, it.key, axis, e.target.value)}
                                    className={`w-28 text-right tabular-nums px-2 py-1.5 rounded-md border outline-none focus:ring-2 focus:ring-[#0056D2] ${
                                      isInvalid ? "bg-red-50 border-red-300" : isDirty ? "bg-yellow-50 border-yellow-300" : "bg-white border-slate-200"
                                    }`}
                                    placeholder="—"
                                    data-testid={`cell-${key}-${it.key}-${axis}`}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-center text-xs text-slate-400">{it.unit}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Sticky-ish action bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 -mx-4 px-4 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid="action-bar">
        <div className="text-sm">
          <span className={modifiedCount === 0 ? "text-slate-500" : "font-semibold text-yellow-700"} data-testid="modified-count">
            {modifiedCount === 0 ? t("adminNumbeo.noFieldsModified") : t("adminNumbeo.fieldsModified", { n: modifiedCount })}
          </span>
          {invalidCells.size > 0 && (
            <span className="ml-3 text-xs text-red-700 inline-flex items-center gap-1" data-testid="invalid-count">
              <AlertCircle className="w-3 h-3" /> {invalidCells.size} valeur(s) invalide(s)
            </span>
          )}
          <span className="ml-3 text-xs text-slate-400">{t("adminNumbeo.nullEmpty")}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(lp("/admin/numbeo"))} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm" data-testid="btn-cancel">{t("adminNumbeo.btnCancel")}</button>
          <button onClick={reset} disabled={modifiedCount === 0} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5" data-testid="btn-reset">
            <RotateCcw className="w-3.5 h-3.5" /> {t("adminNumbeo.btnReset")}
          </button>
          <button onClick={save} disabled={saveDisabled} className={`px-4 py-2 rounded-full text-sm font-medium transition inline-flex items-center gap-1.5 ${saveDisabled ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-[#0056D2] hover:bg-[#0047AD] text-white"}`} data-testid="btn-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? t("adminNumbeo.saving") : t("adminNumbeo.btnSave")}
          </button>
        </div>
      </div>
    </div>
  );
}
